import api from '../utils/api';

export interface Vaccination {
  id: string;
  horseId: string;
  clinicId: string;
  dischargeSummaryId?: string;
  vaccineName: string;
  dateAdministered: string;
  nextDueDate?: string;
  administeredBy?: string;
  notes?: string;
  /** Doses billed and taken off stock (ADR-0038); null = one dose. Billed only when drawn from a lot (ADR-0047). */
  quantity?: number | null;
  /** The lot that went into the horse (ADR-0047): a snapshot, editable. */
  lotNumber?: string | null;
  /** Calendar day: formatCalendarDate, never re-zone. */
  lotExpiresOn?: string | null;
  manufacturer?: string | null;
  /** mL given. */
  doseMl?: number | null;
  /** What the server found when it looked for the lot; read-only. */
  stockIssues?: VaccinationStockIssue[];
  isArchived: boolean;
  archivedAt?: string;
  archivedBy?: string;
  createdAt: string;
  updatedAt: string;
  horse?: any;
  clinic?: any;
}

export type VaccinationStockIssue = 'MISSING_LOT' | 'LOT_NOT_IN_INVENTORY' | 'EXPIRED_LOT' | 'LOT_SHORT';

export interface CreateVaccinationDto {
  horseId: string;
  vaccineName: string;
  dateAdministered: string;
  nextDueDate?: string;
  administeredBy?: string;
  notes?: string;
  quantity?: number;
  /** Stock location the dose deducts from; omit = server infers (truck → warehouse, ADR-0013). */
  dispenseLocationId?: string;
  /** Bottle for the DEA auto-log; omit = server FIFO pick (ADR-0014). */
  deaLotId?: string;
  /** The lot by number; the server finds it at the row this dose resolves to (ADR-0047). */
  lotNumber?: string;
  /** yyyy-MM-dd, for a lot typed by hand. */
  lotExpiresOn?: string;
  manufacturer?: string;
  doseMl?: number;
}

export interface UpdateVaccinationDto {
  vaccineName?: string;
  dateAdministered?: string;
  nextDueDate?: string;
  administeredBy?: string;
  notes?: string;
  quantity?: number | null;
  /** A new lot moves the dose to it in stock (ADR-0047 A3). null clears. */
  lotNumber?: string | null;
  lotExpiresOn?: string | null;
  manufacturer?: string | null;
  doseMl?: number | null;
}

/**
 * A VaccinationDialog row → the create body. One place for the mapping, so every screen that records
 * vaccinations (both horse profiles and the SOAP note) sends the lot fields the same way.
 */
export function toCreateVaccinationDto(row: any, horseId: string): CreateVaccinationDto {
  return {
    horseId,
    vaccineName: row.vaccineName || row.name,
    dateAdministered: row.dateAdministered || new Date().toISOString(),
    nextDueDate: row.nextDueDate,
    administeredBy: row.administeredBy,
    notes: row.notes,
    // The dose billed and taken off stock (ADR-0038); with a lot, billed only (ADR-0047).
    quantity: row.quantity,
    // "Deduct stock from": undefined = Automatic (server infers, ADR-0013).
    dispenseLocationId: row.dispenseLocationId,
    // Bottle for the DEA auto-log; undefined = server FIFO pick (ADR-0014).
    deaLotId: row.deaLotId,
    // The lot by number and the mL given; the server finds the lot (ADR-0047).
    lotNumber: row.lotNumber,
    lotExpiresOn: row.lotExpiresOn,
    manufacturer: row.manufacturer,
    doseMl: row.doseMl,
  };
}

export const VaccinationService = {
  async getAll(params?: {
    horseId?: string;
    startDate?: string;
    endDate?: string;
    includeArchived?: boolean;
  }): Promise<Vaccination[]> {
    const response = await api.get('/vaccinations', { params });
    return response.data;
  },

  async getById(id: string): Promise<Vaccination> {
    const response = await api.get(`/vaccinations/${id}`);
    return response.data;
  },

  async create(data: CreateVaccinationDto): Promise<Vaccination> {
    const response = await api.post('/vaccinations', data);
    return response.data;
  },

  async update(id: string, data: UpdateVaccinationDto): Promise<Vaccination> {
    const response = await api.put(`/vaccinations/${id}`, data);
    return response.data;
  },

  async archive(id: string): Promise<Vaccination> {
    const response = await api.post(`/vaccinations/${id}/archive`);
    return response.data;
  },

  async restore(id: string): Promise<Vaccination> {
    const response = await api.post(`/vaccinations/${id}/restore`);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/vaccinations/${id}`);
  },
};

// Legacy export for backward compatibility
export const vaccinationService = VaccinationService;
